import React from 'react';
import InAppLink from '../components/ui/InAppLink';
import { ROUTE_PATTERNS } from '../router/routes';
import api from '../apiService';

/**
 * Who the customer is, from the one entitlement signal the client actually has.
 *
 * BAR-1351. A customer who buys BARK over MCP gets every web module off and
 * `mcp_access` on, which is exactly what BAR-1148 specifies. They then log in
 * and every door in the app says "contact your BARK representative to unlock
 * it". There is no representative: self-serve is the whole point, and the
 * product they paid for lives in Claude, not here. This module is what lets the
 * locked surfaces tell them that instead.
 */

/**
 * The modules that gate a surface in THIS app.
 *
 * `mcp_access` is deliberately not among them. It is a real module in the
 * backend registry (`shared/authz/modules.py`) but it gates the MCP gateway,
 * not a screen here, so a customer holding only that one has nothing to open.
 */
export const CUSTOMER_MODULE_IDS = [
    'regulatory_research',
    'build_artifact',
    'quick_lookup',
    'project_library',
    'reporting',
    'classification',
] as const;

export type CustomerModuleId = (typeof CUSTOMER_MODULE_IDS)[number];

export const MCP_ACCESS_MODULE_ID = 'mcp_access';

/**
 * The connector guide on the marketing site.
 *
 * A constant, not a `/config.json` value, because it is one public page that is
 * identical for dev and prod. Runtime config exists for per-environment service
 * origins; routing this through Terraform, an env var, `start-nginx.sh` and
 * `apiService` would buy nothing and would add a way for the link to arrive
 * empty for exactly the customer this exists for.
 *
 * BAR-1528: one guide for every assistant BARK connects to (Claude, ChatGPT and
 * Codex), so the name no longer says Claude.
 *
 * The path has no `.html`: the site sets `cleanUrls`, so
 * `/connect-bark-mcp.html` answers 301 and the customer pays a redirect for
 * nothing.
 */
export const CONNECTOR_GUIDE_URL = 'https://barktechnologies.tech/connect-bark-mcp';

/**
 * The marketing site, where people register. An expired activation link is
 * replaced by registering again there (BAR-1528): nobody at BARK issues it.
 */
export const BARK_WEBSITE_URL = 'https://barktechnologies.tech';

/** The address the app already offers elsewhere (see RequestAccessModal). */
export const SUPPORT_EMAIL = 'info@barktechnologies.se';

/**
 * Whether this account's subscription is MCP only: every web module off AND
 * `mcp_access` on.
 *
 * BOTH halves are load-bearing, and the second one is the subtle one.
 *
 * Requiring `mcp_access` to be ON is what separates this customer from a PAUSED
 * one, whose map is all six off (BAR-1286), and from a fully restricted tenant.
 * Those two shapes are indistinguishable from each other here, and neither
 * should be told their product is in Claude, so they keep the generic copy.
 *
 * Requiring ALL FIVE to be off is what keeps a web customer out of this branch.
 * Since 2026-09-11 Build is default-OFF for everyone (BAR-82, Kawi: "no users
 * except Norion can access build"), so an ordinary customer routinely carries
 * `build_artifact` in `disabled_modules` and would match a looser test.
 *
 * Takes the predicate rather than the array so it composes with the one
 * `isModuleDisabled` the app already exposes, and so every existing test mock
 * keeps working unchanged.
 */
export function isMcpOnlyCustomer(isModuleDisabled: (moduleId: string) => boolean): boolean {
    return hasNoWebAccess(isModuleDisabled) && !isModuleDisabled(MCP_ACCESS_MODULE_ID);
}

/** Whether an organization's effective module map grants MCP but no web modules. */
export function isMcpOnlyModuleMap(effective: Record<string, boolean> | undefined): boolean {
    return CUSTOMER_MODULE_IDS.every((moduleId) => !effective?.[moduleId]) &&
        effective?.[MCP_ACCESS_MODULE_ID] === true;
}

/**
 * Whether this account has no web access at all: all five web modules off.
 *
 * BAR-1528. This is exactly the condition on which the orchestrator and
 * report-library refuse EVERY web route with 403 `web_access_not_in_plan`, so
 * the app uses it to decide which calls not to make. A call made anyway only
 * comes back as an error panel, a toast or a spinner that never ends, on a page
 * whose doors are already locked.
 *
 * It deliberately ignores `mcp_access`, because the server does. A paused or
 * suspended organization has all six modules off and is refused the same way.
 * That is also why the copy (who the customer is) branches on
 * `isMcpOnlyCustomer`, and the calls (what the server will answer) branch here.
 */
export function hasNoWebAccess(isModuleDisabled: (moduleId: string) => boolean): boolean {
    return CUSTOMER_MODULE_IDS.every((moduleId) => isModuleDisabled(moduleId));
}

/**
 * Whether Chat for this account is served by the chat-free service: a
 * free-plan account, and only where the service is configured for this
 * environment. Everywhere else (enterprise, or no URL) this is false and
 * nothing changes.
 */
export function usesChatFree(user: { plan?: string } | null | undefined): boolean {
    return user?.plan === 'free' && !!api.getChatFreeServiceUrl();
}

/**
 * Whether Chat is locked: every web module off, unless chat-free serves it.
 * Research and Build keep locking on their own modules.
 */
export function isChatLocked(isModuleDisabled: (moduleId: string) => boolean, chatFree: boolean | undefined): boolean {
    return hasNoWebAccess(isModuleDisabled) && !chatFree;
}

/**
 * What the shared "this is locked" dialog says.
 *
 * Every locked door in the app funnels into one `ConfirmDialog`: the five
 * sidebar entries, both landing cards, and the deep-link gate in ShellRoutes.
 * Branching here is therefore the whole fix for all of them.
 *
 * `onOpenConnect` runs when an MCP-only customer follows the link to the
 * connect page: the caller moves the router and closes the dialog. Without it
 * the link loads `/connect` like any other link.
 */
export function unlockModuleDialogCopy(
    label: string,
    mcpOnly: boolean,
    onOpenConnect?: () => void,
): { title: string; description: React.ReactNode } {
    const moduleName = label || 'This module';

    if (!mcpOnly) {
        return {
            title: `Unlock ${label}`,
            description: `${moduleName} isn't enabled for your account yet. Contact your BARK representative to unlock it.`,
        };
    }

    const linkClass = 'text-accent hover:text-accent-hover underline';

    // BAR-1528: "plan", not "subscription". A free signup lands here too, and
    // has no subscription. The steps to connect an assistant are on a page of
    // this app (`/connect`), which is open to an MCP-only account, so the link
    // stays in the app instead of opening the website guide.
    return {
        title: `${moduleName} is not part of your plan`,
        description: (
            <>
                Your BARK plan works inside Claude, ChatGPT and Codex, not in this web app.{' '}
                <InAppLink href={ROUTE_PATTERNS.connect} onNavigate={onOpenConnect} className={linkClass}>
                    See how to connect BARK to your assistant
                </InAppLink>
                . To add the web app, or if the connector does not work, email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className={linkClass}>
                    {SUPPORT_EMAIL}
                </a>
                .
            </>
        ),
    };
}
